import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { ImageAsset, SiteSummary } from '../../models/cms-models';
import { ImagesService } from '../../services/images-service';
import { SiteContextService } from '../../services/site-context.service';

type ImageSortKey = 'originalFileName' | 'createdAt' | 'updatedAt' | 'landingPageUsageCount' | 'galleryUsageCount';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-images-page',
  imports: [CommonModule, FormsModule],
  templateUrl: './images-page.html',
  styleUrl: './images-page.css',
})
export class ImagesPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private imagesService = inject(ImagesService);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  images: ImageAsset[] = [];
  selectedSiteId: string | null = null;
  uploadAlt = '';
  uploadCaption = '';
  uploadFile: File | null = null;
  metadataAlt = '';
  metadataCaption = '';
  loading = false;
  uploading = false;
  saving = false;
  deletingImageId: string | null = null;
  error: string | null = null;
  isUploadDialogOpen = false;
  isMetadataDialogOpen = false;
  editingImageId: string | null = null;
  sortKey: ImageSortKey = 'createdAt';
  sortDirection: SortDirection = 'desc';
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSiteId]) => {
        void this.syncSelectedSite(sites, activeSiteId);
      });
  }

  get selectedSiteName(): string {
    return this.sites.find((site) => site.id === this.selectedSiteId)?.name ?? 'site';
  }

  get editingImage(): ImageAsset | null {
    return this.images.find((image) => image.id === this.editingImageId) ?? null;
  }

  get sortedImages(): ImageAsset[] {
    return [...this.images].sort((left, right) => this.compareImages(left, right));
  }

  openUploadDialog(): void {
    if (!this.selectedSiteId) {
      return;
    }

    this.resetUploadForm();
    this.isUploadDialogOpen = true;
  }

  closeUploadDialog(): void {
    if (this.uploading) {
      return;
    }

    this.isUploadDialogOpen = false;
    this.resetUploadForm();
  }

  openMetadataDialog(imageId: string): void {
    const image = this.images.find((candidate) => candidate.id === imageId);
    if (!image) {
      return;
    }

    this.editingImageId = imageId;
    this.metadataAlt = image.defaultAlt ?? '';
    this.metadataCaption = image.defaultCaption ?? '';
    this.isMetadataDialogOpen = true;
  }

  closeMetadataDialog(): void {
    if (this.saving) {
      return;
    }

    this.isMetadataDialogOpen = false;
    this.editingImageId = null;
    this.metadataAlt = '';
    this.metadataCaption = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.uploadFile = input.files?.[0] ?? null;
    input.value = '';
  }

  async uploadImage(): Promise<void> {
    if (!this.selectedSiteId || !this.uploadFile) {
      return;
    }

    this.uploading = true;
    this.error = null;

    try {
      const created = await this.imagesService.createImage({
        siteId: this.selectedSiteId,
        defaultAlt: this.cleanOptionalText(this.uploadAlt),
        defaultCaption: this.cleanOptionalText(this.uploadCaption),
        file: this.uploadFile,
      });
      this.images = [created, ...this.images.filter((image) => image.id !== created.id)];
      this.isUploadDialogOpen = false;
      this.resetUploadForm();
    } catch {
      this.error = 'Failed to upload the image.';
    } finally {
      this.uploading = false;
    }
  }

  async saveMetadata(): Promise<void> {
    const image = this.editingImage;
    if (!image) {
      return;
    }

    this.saving = true;
    this.error = null;

    try {
      const updated = await this.imagesService.updateImage(image.id, {
        siteId: image.siteId,
        defaultAlt: this.cleanOptionalText(this.metadataAlt),
        defaultCaption: this.cleanOptionalText(this.metadataCaption),
      });
      this.images = this.images.map((existing) => (existing.id === updated.id ? { ...existing, ...updated } : existing));
      this.openMetadataDialog(updated.id);
    } catch {
      this.error = 'Failed to update image metadata.';
    } finally {
      this.saving = false;
    }
  }

  async deleteImage(imageId: string): Promise<void> {
    const image = this.images.find((candidate) => candidate.id === imageId);
    if (!image || !image.canDelete || this.deletingImageId) {
      return;
    }

    const confirmed = window.confirm(`Delete "${image.originalFileName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    this.deletingImageId = imageId;
    this.error = null;

    try {
      await this.imagesService.deleteImage(imageId);
      this.images = this.images.filter((existing) => existing.id !== imageId);
      if (this.editingImageId === imageId) {
        this.closeMetadataDialog();
      }
    } catch {
      this.error = 'Failed to delete the image. It may still be in use.';
    } finally {
      this.deletingImageId = null;
    }
  }

  setSort(sortKey: ImageSortKey): void {
    if (this.sortKey === sortKey) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortKey = sortKey;
    this.sortDirection = sortKey === 'originalFileName' ? 'asc' : 'desc';
  }

  sortStateFor(sortKey: ImageSortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey !== sortKey) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  sortIndicatorFor(sortKey: ImageSortKey): string {
    if (this.sortKey !== sortKey) {
      return ' ';
    }

    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  private async syncSelectedSite(sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.selectedSiteId = null;
      this.images = [];
      this.loadedSiteId = null;
      this.closeUploadDialog();
      this.closeMetadataDialog();
      return;
    }

    const targetSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!targetSiteId) {
      return;
    }

    this.selectedSiteId = targetSiteId;

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    this.closeUploadDialog();
    this.closeMetadataDialog();
    await this.loadImages(targetSiteId);
  }

  private async loadImages(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.images = await this.imagesService.getSiteImages(siteId);
      this.loadedSiteId = siteId;
    } catch {
      this.images = [];
      this.error = 'Failed to load images for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  private compareImages(left: ImageAsset, right: ImageAsset): number {
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    switch (this.sortKey) {
      case 'originalFileName':
        return left.originalFileName.localeCompare(right.originalFileName) * direction;
      case 'createdAt':
        return (Date.parse(left.createdAt) - Date.parse(right.createdAt)) * direction;
      case 'updatedAt':
        return (Date.parse(left.updatedAt) - Date.parse(right.updatedAt)) * direction;
      case 'landingPageUsageCount':
        return (left.landingPageUsageCount - right.landingPageUsageCount) * direction;
      case 'galleryUsageCount':
        return (left.galleryUsageCount - right.galleryUsageCount) * direction;
      default:
        return 0;
    }
  }

  private resetUploadForm(): void {
    this.uploadAlt = '';
    this.uploadCaption = '';
    this.uploadFile = null;
  }

  private cleanOptionalText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
}
